import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import moment from 'moment';
import useUserInfo from '@src/hooks/useTtsUser';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { checkFeaturePermission, countTextLength } from '@src/services/tts';
import { canUseEOLVoice, checkAdvancedGlobalVoice } from '@src/services/voice';
import { checkVietNam } from '@src/utils/checkCountry';
import { PACKAGE_LEVEL } from '@src/constants/package';
import {
  VALID_CHARACTERS_LENGTH_REGEX,
  VALID_CHARACTERS_REGEX,
} from '@src/constants/tts';
import apis from '@src/apis';
import { WARNING_REQUEST_TYPE } from '@src/constants/request';
import sanitize from 'sanitize-html';
import { DIALOG_V2_TYPE } from '@src/constants';
import { useEffect, useRef } from 'react';
import { VC_VOICE_CODE_REGEX } from '@src/constants/voiceCloning';
import { isBlockChanged } from '@src/services/project';
import { VOICE_PROVIDER } from '@src/constants/voice';

const useSynthesisValidation = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.auth);
  const { usingPackage } = useSelector((state) => state.user);

  const { multiBlock, voiceDefault, audioType } = useSelector(
    (state) => state.project,
  );
  const { ttsUser } = useUserInfo();
  const totalBlockCredits = useRef(0);
  const { getFeatureValue } = useFeatureFlags();
  const voiceHasBeenRenamed = getFeatureValue(
    FEATURE_KEYS.VOICE_HAS_BEEN_RENAMED,
    { userId: user.id, email: user.email, phoneNumber: user.phoneNumber },
  );

  const useUserEolDate = getFeatureValue(
    FEATURE_KEYS.UPDATE_VOICES_BY_USER_EOL_DATE,
    {
      userId: user.id,
    },
  );
  const { packageExpiryDate, remainingCharacters, bonusCharacters } =
    user || {};
  const { studio } = ttsUser;
  const { cycle, topUp, custom } = studio || {};
  const maxLengthInputText = ttsUser?.maxLengthInputText || 0;
  const {
    remainingCredits: remainingCycleCredits = 0,
    // eslint-disable-next-line prefer-const
    isLocked: isCycleCreditLocked,
  } = cycle || {};
  const {
    remainingCredits: remainingTopUpCredits = 0,
    // eslint-disable-next-line prefer-const
    isLocked: isTopUpCreditLocked,
  } = topUp || {};
  const {
    remainingCredits: remainingCustomCredits = 0,
    // eslint-disable-next-line prefer-const
    isLocked: isCustomCreditLocked,
  } = custom || {};
  // const { ip } = useLocation();
  const { blocks, blockCredits, originalBlocks } = multiBlock || {};

  // Credit calculations

  const checkVoicePermission = (voice) => {
    const isVietNam = checkVietNam();
    const isDefaultGlobalVoice =
      !isVietNam &&
      usingPackage?.level === PACKAGE_LEVEL.BASIC &&
      voice?.code === voiceDefault?.code;

    return (
      !voice?.features?.length ||
      isDefaultGlobalVoice ||
      voice.features.every((item) =>
        checkFeaturePermission(ttsUser.features, item),
      ) ||
      (canUseEOLVoice({ voice, user }) && useUserEolDate)
    );
  };

  const getTotalTextLength = (targetBlocks = null) => {
    const blocksToCheck = targetBlocks || Object.values(blocks || {});
    return blocksToCheck.reduce((total, block) => {
      const voice = block?.voice || voiceDefault;
      if (!block?.text) return total;
      return total + countTextLength(block.text) * (voice?.creditFactor || 1);
    }, 0);
  };

  const getInvalidTexts = (targetBlocks = []) => {
    const invalidTexts = targetBlocks.reduce((acc, curr) => {
      const { text, voice = voiceDefault } = curr;
      // Only validate with voice provider is Vbee
      if (voice?.provider !== VOICE_PROVIDER.VBEE) return acc;
      const invalidSentenceText = text.match(VALID_CHARACTERS_LENGTH_REGEX);
      if (invalidSentenceText?.length) return acc?.concat(invalidSentenceText);
      const isValidText = VALID_CHARACTERS_REGEX.test(text);
      if (!isValidText) return acc?.concat([text]);

      return acc;
    }, []);
    return invalidTexts;
  };
  useEffect(() => {
    totalBlockCredits.current = Object.values(blockCredits || {}).reduce(
      (acc, curr) => acc + curr,
      0,
    );
  }, [blockCredits]);

  const validateSynthesis = (options = {}) => {
    const {
      blockData = null, // Single block data for single block synthesis
      onOpenOutOfCreditsDialog = () => {},
      onOpenExpiredPackageDialog = () => {},
      onOpenDialogLockedCycle = () => {},
      onOpenMaxCharsLimitExceeded = () => {},
      onOpenLimitedFeatureDialog = () => {},
      onSetWarningOneTimeCredits = () => {},
      onOpenWrongSyntaxDialog = () => {},
      onOpenWrongSyntaxDialogV2 = () => {},
      onOpenDiscardedVoiceCloning = () => {},
    } = options;

    const isChanged = (block, oldBlock) => {
      const withoutExtension = block?.audioLink?.split('.')?.pop();
      const currentBlock = {
        text: block?.text,
        voiceCode: block?.voiceCode,
        speed: block?.speed,
        audioType: withoutExtension,
      };
      const originalBlock = {
        text: oldBlock[block.id]?.text,
        voiceCode: oldBlock[block.id]?.voiceCode,
        speed: oldBlock[block.id]?.speed,
        audioType,
      };
      return isBlockChanged(currentBlock, originalBlock) || !block?.audioLink;
    };
    // Determine blocks to validate
    const targetBlocks = blockData
      ? [blockData]
      : Object.keys(blocks || {}).map((id) => blocks[id]);
    const blocksWithText = targetBlocks?.filter(
      (block) => !!block?.text && isChanged(block, originalBlocks),
    );

    const blockWithDiscardedVoiceCloning = blocksWithText.find(
      (block) =>
        VC_VOICE_CODE_REGEX.test(block?.voice?.code) &&
        block?.voice?.discardAt &&
        moment().isAfter(block?.voice?.discardAt),
    );

    if (blockWithDiscardedVoiceCloning) {
      onOpenDiscardedVoiceCloning();
      return false;
    }

    const blockWithVoiceInactive = blocksWithText.find(
      (block) => !block?.voice?.active && !!block?.voice?.canonicalVoice?.name,
    );
    const blockWithVoiceEOL = blocksWithText.find(
      (block) =>
        block?.voice?.eolDate && !canUseEOLVoice({ voice: block?.voice, user }),
    );
    const isRenameVoice = voiceHasBeenRenamed && blockWithVoiceInactive;

    if (blockWithVoiceEOL && useUserEolDate) {
      onOpenWrongSyntaxDialogV2({
        name: 'error-syntax',
        variant: 'warning',
        title: t('notice'),
        description: sanitize(
          t('eolVoiceoverHasBeenDiscontinued', {
            voiceName: blockWithVoiceEOL?.voice?.name,
          }),
          {
            allowedTags: ['p', 'strong'],
            allowedAttributes: {
              '*': ['style'],
            },
          },
        ),
        type: DIALOG_V2_TYPE.CANNOT_USE_VOICE,
      });
      return false;
    }

    if (isRenameVoice) {
      onOpenWrongSyntaxDialogV2({
        name: 'error-syntax',
        variant: 'none',
        title: t('notice'),
        description: sanitize(
          t('voiceHasBeenRenamed', {
            newName: blockWithVoiceInactive?.voice?.canonicalVoice?.name,
          }),
          {
            allowedTags: ['strong'],
          },
        ),
        type: DIALOG_V2_TYPE.RENAME_VOICE,
      });
      return false;
    }

    const invalidTexts = getInvalidTexts(blocksWithText);

    if (invalidTexts.length) {
      const invalidText = invalidTexts.join(', ');
      onOpenWrongSyntaxDialog({
        name: 'error-syntax',
        variant: 'warning',
        title: t('errorSyntax'),
        description: t('errorSyntaxDescription'),
        subDescription: t('invalidCharacter', { invalidText }),
      });
      const text = blocksWithText.map((sentence) => sentence.text).join('\n');
      apis.warning.sendWarningInvalidRequest({
        text,
        invalidTexts,
        type: WARNING_REQUEST_TYPE.CHARACTER_EXCEED,
      });

      return false;
    }
    // Check voice permission
    const canUseVoice = blocksWithText.every((block) =>
      checkVoicePermission(block?.voice),
    );
    if (!canUseVoice) {
      onOpenLimitedFeatureDialog('useThisVoice');
      return false;
    }

    // Check input text length
    const countLength =
      getTotalTextLength(blocksWithText) + totalBlockCredits.current;

    if (countLength > maxLengthInputText) {
      onOpenMaxCharsLimitExceeded(true);
      return false;
    }

    // Check package expired
    const isExpiredPackage =
      packageExpiryDate && moment().isAfter(packageExpiryDate);

    if (isExpiredPackage) {
      onOpenExpiredPackageDialog(true);
      return false;
    }

    const isLockedCycle =
      isCycleCreditLocked &&
      !remainingTopUpCredits &&
      !remainingCharacters &&
      !bonusCharacters;

    const isLockedTopUp =
      isTopUpCreditLocked && !remainingCharacters && !bonusCharacters;

    if (isLockedCycle || isLockedTopUp) {
      onOpenDialogLockedCycle({
        title: t('charactersTemporarilyLocked'),
        description: t('descriptionPopupLockedCredit'),
      });
      return false;
    }

    let usageRemainingCharacters = remainingCharacters;
    let actualRemainingCycleCredits = remainingCycleCredits;
    let actualRemainingTopUpCredits = remainingTopUpCredits;
    let actualRemainingCustomCredits = remainingCustomCredits;

    if (isCycleCreditLocked) actualRemainingCycleCredits = 0;
    if (isTopUpCreditLocked) actualRemainingTopUpCredits = 0;
    if (isCustomCreditLocked) actualRemainingCustomCredits = 0;

    // Check advanced global voice usage
    const hasAdvancedGlobalVoice = blocksWithText.some((block) => {
      const useLockOneTimeCreditsAdvancedVoice = getFeatureValue(
        FEATURE_KEYS.LOCK_ONE_TIME_CREDITS_ADVANCED_VOICE,
        {
          userId: user.id,
          voiceCode: block?.voiceCode,
          voiceName: block?.voiceName,
        },
      );
      return (
        checkAdvancedGlobalVoice(block?.voice) &&
        useLockOneTimeCreditsAdvancedVoice
      );
    });

    if (hasAdvancedGlobalVoice) usageRemainingCharacters = 0;

    const totalCredits =
      usageRemainingCharacters +
      bonusCharacters +
      actualRemainingCycleCredits +
      actualRemainingTopUpCredits +
      actualRemainingCustomCredits;

    if (countLength > totalCredits) {
      if (remainingCharacters > 0 && usageRemainingCharacters === 0) {
        onSetWarningOneTimeCredits(true);
      }
      onOpenOutOfCreditsDialog(true);
      return false;
    }

    return true;
  };

  return {
    validateSynthesis,
    getTotalTextLength,
  };
};

export default useSynthesisValidation;
